import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Box, Collapse, Grid, IconButton, Typography } from '@mui/material';
import { pick, startCase } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';

import { PostContactInfoPayload, usePostContactInfo, useUserAccount } from '@/api/userAccount';
import { FormInputText } from '@/components/form/FormInputText';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { borderGrey } from '@/theme';

const contactInfoFields = ['firstName', 'lastName', 'phone', 'organization', 'address'];
const contactInfoDefaultValues = contactInfoFields.reduce(
    (acc, key) => ({ ...acc, [key]: '' }),
    {} as Record<string, string>
);

export const ContactInfoSection = () => {
    const { toggle: toggleEditMode, isOpen: isEditMode, close: disableEditMode } = useDisclosure();
    const { data: accountData } = useUserAccount();
    const { mutateAsync: postContactInfo } = usePostContactInfo();
    const mixpanel = useMixpanel();
    const {
        control: contactInfoControl,
        reset: contactInfoReset,
        handleSubmit: contactInfoHandleSubmit,
        formState: contactInfoFormState,
    } = useForm<PostContactInfoPayload>({
        defaultValues: contactInfoDefaultValues,
        mode: 'onChange',
    });
    useUnsavedChangesGuard(contactInfoFormState.isDirty, 'contact-info');

    const onContactInfoSubmit = async (formData: PostContactInfoPayload) => {
        try {
            await postContactInfo(formData);
            mixpanel?.track('User Account - Contact Info Updated');
        } catch (err) {
            mixpanel?.track('User Account - Contact Info Update Failed');
            console.error(err);
        }
        disableEditMode();
    };

    const onEditClick = () => {
        toggleEditMode();
        onContactInfoReset();
    };

    const onContactInfoReset = () => {
        contactInfoReset(pick(accountData?.data.user, Object.keys(contactInfoDefaultValues)));
    };

    useEffect(() => {
        contactInfoReset(pick(accountData?.data.user, Object.keys(contactInfoDefaultValues)));
    }, [accountData?.data.user, contactInfoReset]);

    return (
        <Box data-testid="user-account-contact-info-section">
            <Typography color="text.secondary" variant="h5" sx={{ mb: 2 }}>
                Contact Information{' '}
                <IconButton
                    size="small"
                    sx={{ display: 'relative', bottom: 5 }}
                    onClick={onEditClick}
                    data-testid="contact-info-edit-mode-button"
                >
                    {isEditMode ? <ExpandLessIcon /> : <EditIcon />}
                </IconButton>
            </Typography>

            <Grid container spacing={3}>
                {Object.keys(contactInfoDefaultValues).map((key) => (
                    <Grid size={{ xs: 12, md: 6 }} key={key}>
                        <FormInputText
                            name={key}
                            control={contactInfoControl}
                            label={startCase(key)}
                            inputFieldProps={{
                                id: key,
                                type: 'text',
                                size: 'medium',
                                multiline: key === 'address',
                                disabled: !isEditMode,
                                sx: {
                                    '&& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isEditMode ? 'info.main' : borderGrey,
                                    },
                                    '&& .MuiFormLabel-root:not(.Mui-disabled)': {
                                        color: 'info.main',
                                    },
                                    '&& .MuiOutlinedInput-input.Mui-disabled': {
                                        WebkitTextFillColor: 'black',
                                    },
                                },
                            }}
                            controllerProps={{
                                rules: {
                                    required: 'Field is required',
                                },
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
            <Collapse in={isEditMode}>
                <ResetUpdateButtonGroup
                    onReset={onContactInfoReset}
                    onSubmit={contactInfoHandleSubmit(onContactInfoSubmit)}
                    isResetDisabled={!contactInfoFormState.isDirty || contactInfoFormState.isSubmitting}
                    isUpdateDisabled={!contactInfoFormState.isDirty || contactInfoFormState.isSubmitting}
                    isSubmitting={contactInfoFormState.isSubmitting}
                />
            </Collapse>
        </Box>
    );
};
